import { ReactElement } from 'react';
import { Location } from 'pages/locations';
import ScheduleAppointmentCta from 'components/UI/ScheduleAppointmentCta';
import { formatPhoneNumber } from 'lib/util';
import RichText from './Richtext';
import CTA from './Cta';
import type { Document as ContentfulDocument } from '@contentful/rich-text-types';

type LocationCardProps = {
  location: Location & {
    insuranceSlug?: string;
  };
  className?: string;
  customHeader?: ContentfulDocument;
  factua?: boolean;
};

export default function LocationDetailsHeader({
  location,
  className = '',
  customHeader,
  factua = false,
}: LocationCardProps): ReactElement {
  const cityState = [location?.city, location?.state].filter(Boolean).join(', ');
  const address = [
    location?.address1,
    location?.address2,
    location?.address3,
    [cityState, location?.zipCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`py-8.5 lg:py-16 px-4 lg:px-0 ${className}`}>
      <div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
        <div className="w-full lg:max-w-3xl">
          <div className="bordered-heading-richtext bh-location-heading flex items-center text-left">
            {customHeader ? (
              <RichText document={customHeader} classNames="custom-header" />
            ) : (
              <h1 className="text-new-34 md:text-4xl lg:text-5xl font-light leading-tight text-primary-actual mb-0">
                Your <b>{location.name}</b> Eye Clinic
              </h1>
            )}
          </div>
        </div>

        <div className="w-full lg:max-w-2xl">
          <div className="flex flex-col items-start gap-3 text-left">
            {address ? (
              <div className="flex items-start gap-3 text-primary-actual">
                <IconBadge>
                  <LocationPinIcon />
                </IconBadge>
                <span className="text-lg leading-snug">{address}</span>
              </div>
            ) : null}

            {location?.phoneNumber ? (
              <div className="flex items-start gap-3">
                <IconBadge>
                  <PhoneIcon />
                </IconBadge>
                <a
                  className="text-lg text-secondary-actual underline leading-snug"
                  href={`tel:${location?.phoneNumber}`}
                >
                  {formatPhoneNumber(location.phoneNumber)}
                </a>
              </div>
            ) : null}

            <div className="flex w-full flex-col gap-3 pt-2 sm:w-auto sm:flex-row sm:flex-wrap">
              <ScheduleAppointmentCta
                location={location}
                colorScheme="Primary"
                wrapperClasses=""
                classNames="px-6 whitespace-nowrap"
                factua={factua}
                from="location"
                text="Schedule An Eye Exam"
              />
              <CTA
                slug={'#'}
                colorScheme="Outlined"
                backgroundColor="secondary-actual"
                text="Accepted Insurances"
                classNames="px-6 whitespace-nowrap"
                onClick={() => {
                  const acceptedInsurancesSection = document.getElementById(
                    'accepted-insurances-section'
                  );
                  if (acceptedInsurancesSection) {
                    // Remove height styling if present
                    acceptedInsurancesSection.style.removeProperty('height');

                    // Find the <header> tag and get its height
                    const header = document.querySelector('header');
                    const headerHeight = header ? header.getBoundingClientRect().height : 0;

                    // Get the section's position relative to the viewport, add current scroll position, then offset by header height
                    const sectionTop =
                      acceptedInsurancesSection.getBoundingClientRect().top + window.pageYOffset;
                    const scrollToPosition = sectionTop - headerHeight;

                    window.scrollTo({
                      top: scrollToPosition,
                      behavior: 'smooth',
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function IconBadge({ children }: { children: ReactElement }): ReactElement {
  return (
    <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-secondary">
      {children}
    </span>
  );
}

function LocationPinIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-5 w-5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 18.333C10 18.333 16.667 13.889 16.667 8.704C16.667 6.936 15.965 5.24 14.714 3.99C13.464 2.739 11.768 2.037 10 2.037C8.232 2.037 6.536 2.739 5.286 3.99C4.035 5.24 3.333 6.936 3.333 8.704C3.333 13.889 10 18.333 10 18.333Z"
        stroke="white"
        strokeWidth="1.667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 10.926C11.228 10.926 12.222 9.931 12.222 8.704C12.222 7.477 11.228 6.482 10 6.482C8.773 6.482 7.778 7.477 7.778 8.704C7.778 9.931 8.773 10.926 10 10.926Z"
        stroke="white"
        strokeWidth="1.667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PhoneIcon(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-5 w-5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.5 14.117V16.617C17.5 16.948 17.369 17.266 17.136 17.5C16.902 17.734 16.584 17.867 16.253 17.869C13.394 17.557 10.648 16.579 8.253 15.017C6.024 13.601 4.134 11.711 2.718 9.482C1.151 7.076 0.173 4.318 0.866 1.433C0.868 1.102 1 0.784 1.234 0.55C1.468 0.316 1.785 0.184 2.117 0.183H4.617C5.194 0.177 5.684 0.593 5.77 1.163C5.922 2.187 6.19 3.19 6.57 4.152C6.73 4.557 6.635 5.018 6.325 5.326L5.267 6.383C6.452 8.47 8.179 10.198 10.267 11.383L11.323 10.326C11.632 10.017 12.093 9.921 12.498 10.08C13.46 10.461 14.463 10.728 15.487 10.88C16.064 10.968 16.483 11.468 16.47 12.051L17.5 14.117Z"
        stroke="white"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
